/**
 * Display order for multiple-choice options (#150).
 *
 * Seeds put the answer key first in most questions, so options are shown in a
 * shuffled order. The shuffle is display-only: an option keeps its stored
 * letter as its value, so submissions, stored `user_answer` values, answer
 * matching and rescoring all stay in stored order.
 */

/** FNV-1a hash of a string, used to seed the shuffle. */
function hashSeed(seed: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** Mulberry32: a small deterministic generator returning floats in [0, 1). */
function mulberry32(state: number): () => number {
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Builds the shuffle seed for one question in an attempt, so every view of the
 * attempt (quiz, review, flag modal) shows the same order.
 */
export function optionSeed(attemptId: string | undefined, questionId: number | string): string | undefined {
  return attemptId ? `${attemptId}:${questionId}` : undefined;
}

/**
 * Returns the stored option indices in display order. The same seed always
 * gives the same order, so a resumed quiz and its review page match what the
 * student saw. Without a seed, stored order is kept.
 *
 * @param count - the number of options
 * @param seed - identifies the question within an attempt, e.g. `${attemptId}:${questionId}`
 */
export function shuffledOptionOrder(count: number, seed: string | undefined): number[] {
  const order = Array.from({ length: count }, (_, i) => i);
  if (!seed) return order;

  const random = mulberry32(hashSeed(seed));
  for (let i = count - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  return order;
}
